import { OptionType } from '../NavigationMenu';

/**
 * Filters options based on a search term (case-insensitive)
 */
export const filterOptions = (options: OptionType[], searchTerm: string): OptionType[] => {
	if (!searchTerm.trim()) {
		return options;
	}

	const lowerSearchTerm = searchTerm.toLowerCase();
	return options.filter((option) => option.label.toLowerCase().includes(lowerSearchTerm));
};

/**
 * Gets display text for the trigger button
 */
export const getDisplayText = (selectedValue: OptionType | null, placeholder: string): string => {
	if (selectedValue?.value && selectedValue?.label) {
		return selectedValue.label;
	}
	return placeholder;
};

/**
 * Calculates the next focused index for arrow key navigation
 */
export const getNextFocusedIndex = (
	direction: 'up' | 'down',
	currentIndex: number,
	optionsLength: number
): number => {
	if (optionsLength === 0) {
		return -1;
	}

	if (direction === 'down') {
		return currentIndex < optionsLength - 1 ? currentIndex + 1 : 0;
	}
	return currentIndex > 0 ? currentIndex - 1 : optionsLength - 1;
};

/**
 * Checks if an option is selected
 */
export const isOptionSelected = (option: OptionType, selectedValue: OptionType | null): boolean => {
	return option.value === selectedValue?.value;
};

/**
 * Determines if a key press should open the dropdown
 */
export const shouldOpenDropdown = (key: string, isOpen: boolean): boolean => {
	return !isOpen && (key === 'Enter' || key === ' ' || key === 'ArrowDown');
};

/**
 * Determines if a key press should close the dropdown
 * Note: Tab is handled separately in the main keyboard handler
 */
export const shouldCloseDropdown = (key: string, isOpen: boolean): boolean => {
	return isOpen && key === 'Escape';
};

/**
 * Gets the option to select based on current state and key press
 */
export const getOptionToSelect = (
	key: string,
	focusedIndex: number,
	filteredOptions: OptionType[],
	isOpen: boolean
): OptionType | null => {
	if (key !== 'Enter' || !isOpen) {
		return null;
	}

	if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
		return filteredOptions[focusedIndex];
	}

	return null;
};

/**
 * Resets component state for dropdown closure
 */
export const getResetState = (): { isOpen: boolean; searchTerm: string; focusedIndex: number } => ({
	isOpen: false,
	searchTerm: '',
	focusedIndex: -1
});
